import { useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import styled from 'styled-components';

// Components
import { IconArrowRight, IconFlag } from '@u21/tabler-icons';
import { U21SideMenu } from 'app/shared/u21-ui/components/dashboard';
import {
  U21Button,
  U21Chip,
  U21ShowMoreList,
  U21Spacer,
  U21Subsection,
  U21TitleCountLabel,
  U21Typography,
} from 'app/shared/u21-ui/components';
import { EntityRiskScore } from 'app/modules/riskScore/components/EntityRiskScore';
import { SidebarDataRow } from 'app/modules/sidebar/components/utils/SidebarDataRow';
import { SidebarSummary } from 'app/modules/sidebar/components/utils/SidebarSummary';
import { SidebarRiskScoreTimeline } from 'app/modules/riskScore/components/SidebarRiskScoreTimeline';

// Models
import { EntitySidebarDefinition } from 'app/modules/sidebar/models';
import { FullEntityResponse } from 'app/modules/entities/responses';
import { SummaryViewConfigTypeClassifier } from 'app/modules/summarySettings/types';

// Utils
import { formatDate } from 'app/shared/utils/date';
import { sortByTime } from 'app/modules/dataExplorer/helpers';
import { ROUTES_MAP } from 'app/shared/utils/routes';
import { computeDlHashIdForCgdo } from 'app/shared/utils/dlHashId';

// Selectors
import {
  selectDispositionActions,
  selectEnableCgdoCommentsEnabled,
  selectDLEnabledForEntityRiskScore,
  selectHistoricRiskScoresEnabled,
} from 'app/shared/featureFlags/selectors';
import { selectRiskRatingsFeatureEnabled } from 'app/modules/orgSettings/selectors';

import { useGetEntityByExternalId } from 'app/modules/entities/queries/useGetEntityByExternalId';
import { useGetBlockedReasons } from 'app/modules/entities/queries/useGetBlockedReasons';
import { useFetchEntityRiskScore } from 'app/modules/riskScore/queries/useFetchEntityRiskScore';

// Constants
import { INITIAL_ENTITY } from 'app/modules/entities/constants';
import { useEntityAlerts } from 'app/modules/entities/queries/useEntityAlerts';
import { SidebarCommentsCollapsible } from 'app/modules/sidebar/components/SidebarCommentsCollapsible';
import { SidebarDataLabels } from 'app/modules/sidebar/components/SidebarDataLabels';
import { useEntityDetails } from 'app/modules/entities/queries/useEntityDetails';
import { SidebarExternalLinkList } from 'app/modules/sidebar/components/SidebarExternalLinkList';
import {
  selectHasReadAlertsPermission,
  selectAgentOrgID,
} from 'app/modules/session/selectors';

interface OwnProps {
  data: EntitySidebarDefinition['data'];
}

export const SidebarEntity = ({ data }: OwnProps) => {
  const [showEmptyFields, setShowEmptyFields] = useState(false);

  const isDispositionActionsEnabled = useSelector(selectDispositionActions);
  const enableCgdoComments = useSelector(selectEnableCgdoCommentsEnabled);
  const useCacheEndpoint = useSelector(selectDLEnabledForEntityRiskScore);
  const orgId = useSelector(selectAgentOrgID);
  const historicRiskScoresEnabled = useSelector(
    selectHistoricRiskScoresEnabled,
  );
  const riskRatingsFeatureEnabled = useSelector(
    selectRiskRatingsFeatureEnabled,
  );

  const dlHashId = useMemo(
    () =>
      data.externalId ? computeDlHashIdForCgdo(orgId, data.externalId) : '',
    [data.externalId, orgId],
  );

  // FF: CM_DELTA_LAKE_ENTITY_RISK_SCORE — when the sidebar is opened with only an
  // external_id (e.g. risk ratings table), use the cache-compatible endpoint via
  // dlHashId. Otherwise fall back to the unit21 id path.
  const entityDetailsId =
    data.externalId && useCacheEndpoint ? dlHashId : String(data.id);
  const {
    data: entityByDetails = INITIAL_ENTITY,
    isLoading: entityByDetailsLoading,
  } = useEntityDetails(entityDetailsId);

  // DEPRECATED: non-cache external_id lookup, kept for FF=off path.
  const {
    data: entityByExternalId = INITIAL_ENTITY,
    isLoading: entityByExternalIdLoading,
  } = useGetEntityByExternalId(
    data.externalId || '',
    Boolean(data.externalId) && !useCacheEndpoint,
  );

  const [entity, entityLoading] = useMemo((): [FullEntityResponse, boolean] => {
    if (data.externalId && !useCacheEndpoint) {
      return [entityByExternalId, entityByExternalIdLoading];
    }
    return [entityByDetails, entityByDetailsLoading];
  }, [
    data.externalId,
    useCacheEndpoint,
    entityByDetails,
    entityByDetailsLoading,
    entityByExternalId,
    entityByExternalIdLoading,
  ]);

  const { id, tags, comments, external_id: externalId } = entity;

  const tagIds = useMemo(() => tags.map((i) => i.id), [tags]);

  const { data: blockedReasonsResponse, isLoading: loadingBlockedReasons } =
    useGetBlockedReasons(externalId);

  const { blocked_reasons: blockedReasons } = blockedReasonsResponse || {};
  const isBlocked = blockedReasons && blockedReasons.length > 0;

  const { data: riskScore } = useFetchEntityRiskScore(externalId || '');

  const { data: entityAlerts } = useEntityAlerts({
    entityExternalId: externalId,
    payload: {
      limit: 100,
      offset: 1,
    },
  });
  const hasReadAlertsPermission = useSelector(selectHasReadAlertsPermission);
  const idForButtonLink = data.id || id;

  return (
    <U21SideMenu
      actionButtonProps={{
        children: 'Go to Detail Page',
        endIcon: <IconArrowRight />,
        disabled: idForButtonLink === INITIAL_ENTITY.id,
        // data.id has higher priority than entity.id because data.id is immediately available, whereas entity.id only available on entity load.
        // But data.id is not available when the sidebar is shown from the alert's group-by-entity page, so we fall back to entity.id in this case.
        to: ROUTES_MAP.entitiesId.path.replace(':id', String(idForButtonLink)),
      }}
      footerLeftContent={
        <U21Button
          onClick={() => setShowEmptyFields((prev) => !prev)}
          variant="ghost"
        >
          {showEmptyFields ? 'Hide' : 'Show'} Empty Fields
        </U21Button>
      }
      loading={entityLoading}
      noPadding
      title="Entity Details"
    >
      <SidebarSummary
        classifier={SummaryViewConfigTypeClassifier.ENTITY}
        showEmptyFields={showEmptyFields}
        details={entity}
      />
      <U21Spacer spacing={0.5}>
        {riskScore?.riskModel && (
          <U21Subsection
            shadedHeader
            collapsed
            collapsible={historicRiskScoresEnabled && riskRatingsFeatureEnabled}
            title={
              <U21Spacer horizontal>
                <span>Risk Score</span>
                <EntityRiskScore entityExternalId={externalId} />
              </U21Spacer>
            }
          >
            {historicRiskScoresEnabled && riskRatingsFeatureEnabled && (
              <SidebarRiskScoreTimeline
                entityExternalId={externalId}
                entityId={idForButtonLink}
              />
            )}
          </U21Subsection>
        )}
        {isDispositionActionsEnabled && !loadingBlockedReasons && (
          <U21Subsection
            shadedHeader
            collapsible={isBlocked}
            title={
              <U21Spacer horizontal>
                <span>Blocked</span>
                <U21Chip
                  color={isBlocked ? 'error' : 'success'}
                  variant="ghost"
                >
                  {isBlocked ? 'Yes' : 'No'}
                </U21Chip>
              </U21Spacer>
            }
          >
            {isBlocked && (
              <SidebarDataRow label="Reasons" showEmptyFields={showEmptyFields}>
                <U21ShowMoreList value={blockedReasons} />
              </SidebarDataRow>
            )}
          </U21Subsection>
        )}
        {hasReadAlertsPermission && (
          <U21Subsection
            collapsible
            shadedHeader
            title={
              <U21TitleCountLabel count={entityAlerts?.count || 0} label="">
                Flagged Alerts
              </U21TitleCountLabel>
            }
            titleIcon={<IconFlag />}
          >
            {entityAlerts && entityAlerts.count > 0 ? (
              <SidebarExternalLinkList
                list={sortByTime(entityAlerts.alerts, 'start_date').map(
                  (i) => ({
                    id: i.id,
                    label: formatDate(i.start_date),
                    route: ROUTES_MAP.alertsId.path.replace(
                      ':id',
                      String(i.id),
                    ),
                    value: i.title,
                  }),
                )}
              />
            ) : (
              <StyledU21Typography variant="caption" color="text.secondary">
                No flagged alerts
              </StyledU21Typography>
            )}
          </U21Subsection>
        )}
        <SidebarDataLabels ids={tagIds} />
        {enableCgdoComments && (
          <SidebarCommentsCollapsible comments={comments} />
        )}
      </U21Spacer>
    </U21SideMenu>
  );
};

const StyledU21Typography = styled(U21Typography)`
  margin-bottom: 8px;
`;
